// HTTP-клієнт API: база '/api' (Vite проксює на 5340), JSON, credentials 'include' (refresh-cookie, D34).
// Access-токен живе ЛИШЕ в пам'яті. 401 → один спільний refresh (single-flight у вкладці + Web Locks між вкладками,
// щоб дві вкладки не пред'явили той самий refresh-токен і reuse-detection не анулював сесію) → повтор запиту.
// Невдалий refresh → сесію очищено, onSessionExpired (AuthProvider → /login). Помилки — українською (ApiError).
import type { AuthSession } from './types';

export const API_BASE = '/api';

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  /** Усі повідомлення сервера (class-validator повертає масив). */
  readonly messages: string[];
  readonly body: unknown;

  constructor(status: number, message: string, opts: { code?: string; messages?: string[]; body?: unknown } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = opts.code;
    this.messages = opts.messages ?? [message];
    this.body = opts.body;
  }

  get isNetwork(): boolean {
    return this.status === 0;
  }
  get isNotFound(): boolean {
    return this.status === 404;
  }
  get isForbidden(): boolean {
    return this.status === 403;
  }
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/** Людське повідомлення з будь-якої помилки. */
export function errorMessage(e: unknown, fallback = 'Сталася помилка. Спробуйте ще раз.'): string {
  if (e instanceof ApiError) return e.message || fallback;
  if (e instanceof Error && e.message && !/^[A-Za-z ]+$/.test(e.message)) return e.message;
  return fallback;
}

// ---------------------------------------------------------------- стан сесії

let accessToken: string | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

// Позначка «у цьому браузері є сесія» (не секрет, лише підказка бутстрапу): без неї гість на /login і /p не робить
// POST /auth/refresh, який завжди дає 401 і помилку в консолі. Сховище недоступне — поводимось як раніше (пробуємо refresh).
const SESSION_HINT_KEY = 'unisched.session';

export function hasSessionHint(): boolean {
  try {
    return window.localStorage.getItem(SESSION_HINT_KEY) === '1';
  } catch {
    return true;
  }
}

export function setSessionHint(active: boolean): void {
  try {
    if (active) window.localStorage.setItem(SESSION_HINT_KEY, '1');
    else window.localStorage.removeItem(SESSION_HINT_KEY);
  } catch {
    // приватний режим / заблоковане сховище — підказка просто не діє
  }
}

export interface ApiHandlers {
  /** Refresh не вдався (сесія недійсна). */
  onSessionExpired?: () => void;
  /** Успішний refresh — свіжий користувач. */
  onRefreshed?: (session: AuthSession) => void;
}

const handlers: ApiHandlers = {};

export function setApiHandlers(h: ApiHandlers): void {
  Object.assign(handlers, h);
}

// Ендпоінти, де 401 означає «невірні дані», а не «протух access-токен».
const NO_REFRESH_PATHS = ['/auth/login', '/auth/refresh', '/auth/logout'];

function skipsRefresh(path: string): boolean {
  return NO_REFRESH_PATHS.some((p) => path.startsWith(p));
}

export type RefreshResult =
  | { kind: 'ok'; session: AuthSession }
  | { kind: 'invalid' }
  /** Сервер недоступний (мережа / 5xx / проксі без API). */
  | { kind: 'network' }
  /** 429 — ліміт запитів; сесія, можливо, ще дійсна. */
  | { kind: 'limited'; message: string };

let refreshing: Promise<RefreshResult> | null = null;

// Остання дія користувача (мс epoch), спільна для вкладок: пише SessionIdleGuard (U16) і вхід. Сервер за заголовком
// X-User-Idle-Ms завершує сеанс після бездіяльності (ТЗ 4.1.5.3). Фонове опитування активністю не є.
const LAST_ACTIVITY_KEY = 'unisched.lastActivity';

export function markUserActivity(at = Date.now()): void {
  try {
    window.localStorage.setItem(LAST_ACTIVITY_KEY, String(at));
  } catch {
    // сховище недоступне — сервер бездіяльність не перевіряє
  }
}

function idleHeader(): Record<string, string> {
  try {
    const last = Number(window.localStorage.getItem(LAST_ACTIVITY_KEY));
    return Number.isFinite(last) && last > 0 ? { 'X-User-Idle-Ms': String(Math.max(0, Date.now() - last)) } : {};
  } catch {
    return {};
  }
}

async function doRefresh(): Promise<RefreshResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json', ...idleHeader() },
    });
  } catch {
    return { kind: 'network' };
  }
  if (res.status === 429) return { kind: 'limited', message: (await toApiError(res)).message };
  if (res.status >= 500) return { kind: 'network' };
  if (!res.ok) {
    accessToken = null;
    setSessionHint(false);
    return { kind: 'invalid' };
  }
  try {
    const session = (await res.json()) as AuthSession;
    if (!session?.accessToken) return { kind: 'invalid' };
    accessToken = session.accessToken;
    setSessionHint(true);
    handlers.onRefreshed?.(session);
    return { kind: 'ok', session };
  } catch {
    return { kind: 'invalid' };
  }
}

/** Оновлення сесії за refresh-cookie (single-flight у вкладці; між вкладками — Web Locks). */
export function refreshSession(): Promise<RefreshResult> {
  if (refreshing) return refreshing;
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  const run: Promise<RefreshResult> = locks
    ? (locks.request('unisched-auth-refresh', () => doRefresh()) as unknown as Promise<RefreshResult>)
    : doRefresh();
  refreshing = run.finally(() => {
    refreshing = null;
  });
  return refreshing;
}

function sessionExpired(): void {
  accessToken = null;
  if (handlers.onSessionExpired) handlers.onSessionExpired();
  else if (!window.location.pathname.startsWith('/login')) window.location.assign('/login');
}

// ---------------------------------------------------------------- запити

export type QueryValue = string | number | boolean | null | undefined | readonly (string | number)[];
export type QueryParams = Record<string, QueryValue>;

/** Рядок запиту: порожні значення пропускаються, масиви — повторюваним ключем. */
export function buildQuery(params?: QueryParams): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const v of value) sp.append(key, String(v));
    } else {
      sp.set(key, String(value));
    }
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export interface RequestOptions {
  params?: QueryParams;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Не пробувати refresh на 401 (для власних auth-запитів). */
  noRefresh?: boolean;
}

const NETWORK_MESSAGE = "Немає зв'язку з сервером. Перевірте мережу і спробуйте ще раз.";

async function send(method: string, path: string, opts: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json', ...opts.headers };
  let body: BodyInit | undefined;
  if (opts.body instanceof FormData) {
    body = opts.body; // multipart: Content-Type з boundary ставить браузер
  } else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  try {
    return await fetch(`${API_BASE}${path}${buildQuery(opts.params)}`, {
      method,
      headers,
      body,
      credentials: 'include',
      signal: opts.signal,
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new ApiError(0, NETWORK_MESSAGE);
  }
}

const STATUS_MESSAGES: Record<number, string> = {
  400: 'Некоректні дані запиту.',
  401: 'Потрібно увійти в систему.',
  403: 'Недостатньо прав для цієї дії.',
  404: 'Не знайдено.',
  409: 'Конфлікт даних: запис уже змінено або такий уже існує.',
  413: 'Файл завеликий.',
  415: 'Непідтримуваний формат файлу.',
  422: 'Дані не пройшли перевірку.',
  429: 'Забагато запитів. Зачекайте і спробуйте ще раз.',
  500: 'Помилка сервера. Спробуйте пізніше.',
  502: 'Сервер тимчасово недоступний.',
  503: 'Сервіс тимчасово недоступний.',
  504: 'Сервер не відповідає. Спробуйте пізніше.',
};

export async function toApiError(res: Response): Promise<ApiError> {
  let body: unknown;
  try {
    const text = await res.text();
    body = text ? JSON.parse(text) : undefined;
  } catch {
    body = undefined;
  }
  let messages: string[] = [];
  let code: string | undefined;
  if (body && typeof body === 'object') {
    const b = body as { message?: unknown; code?: unknown; error?: unknown };
    if (Array.isArray(b.message)) messages = b.message.map(String);
    else if (typeof b.message === 'string' && b.message) messages = [b.message];
    if (typeof b.code === 'string') code = b.code;
  }
  const fallback = STATUS_MESSAGES[res.status] ?? `Помилка ${res.status}`;
  // Англійські системні повідомлення (Unauthorized, Not Found, Internal server error …) — українською.
  messages = messages.filter((m) => !/^[A-Za-z0-9 .,'"()_-]+$/.test(m));
  if (!messages.length) messages = [fallback];
  return new ApiError(res.status, messages.join('\n'), { code, messages, body });
}

/** Виконує запит з refresh-повтором; повертає успішну Response або кидає ApiError. */
export async function execute(method: string, path: string, opts: RequestOptions = {}): Promise<Response> {
  let res = await send(method, path, opts);

  if (res.status === 401 && !opts.noRefresh && !skipsRefresh(path)) {
    const r = await refreshSession();
    if (r.kind === 'network') throw new ApiError(0, NETWORK_MESSAGE);
    if (r.kind === 'limited') throw new ApiError(429, r.message);
    if (r.kind === 'invalid') {
      sessionExpired();
      throw await toApiError(res);
    }
    // Токен свіжий: повторний 401 — смислова відповідь, а не вихід.
    res = await send(method, path, opts);
  }

  if (!res.ok) throw await toApiError(res);
  return res;
}

async function parseBody<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  const type = res.headers.get('Content-Type') ?? '';
  if (type.includes('json')) return JSON.parse(text) as T;
  return text as unknown as T;
}

export async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const res = await execute(method, path, opts);
  return parseBody<T>(res);
}

type BodyOpts = Omit<RequestOptions, 'body'>;

export const api = {
  get: <T>(path: string, params?: QueryParams, opts?: Omit<RequestOptions, 'params' | 'body'>) =>
    request<T>('GET', path, { ...opts, params }),
  post: <T = void>(path: string, body?: unknown, opts?: BodyOpts) => request<T>('POST', path, { ...opts, body }),
  patch: <T = void>(path: string, body?: unknown, opts?: BodyOpts) => request<T>('PATCH', path, { ...opts, body }),
  put: <T = void>(path: string, body?: unknown, opts?: BodyOpts) => request<T>('PUT', path, { ...opts, body }),
  del: <T = void>(path: string, opts?: RequestOptions) => request<T>('DELETE', path, opts),
  /** multipart/form-data (Content-Type з boundary ставить браузер). */
  upload: <T>(path: string, form: FormData, opts?: BodyOpts & { method?: 'POST' | 'PUT' | 'PATCH' }) =>
    request<T>(opts?.method ?? 'POST', path, { ...opts, body: form }),
  /** Один файл у полі `field` (за замовчуванням 'file') + додаткові поля форми. */
  uploadFile: <T>(path: string, file: File | Blob, opts?: BodyOpts & { field?: string; fields?: Record<string, string> }) => {
    const form = new FormData();
    form.append(opts?.field ?? 'file', file, file instanceof File ? file.name : 'file');
    for (const [k, v] of Object.entries(opts?.fields ?? {})) form.append(k, v);
    return request<T>('POST', path, { ...opts, body: form });
  },
};

/** Стандартна відповідь списку (CONVENTIONS §2). */
export interface Paged<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}
